import React, { useEffect, useState } from "react";
import clsx from "clsx";
import PropTypes from "prop-types";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import A_TextField from "../../../Atoms/Inputs/TextFields/A_TextField";
import A_SimpleSelect from "../../../Atoms/Inputs/Selects/A_SimpleSelect";
import A_Switch from "../../../Atoms/Inputs/Switches/A_Switch";
import IconAction from "./IconAction";
import { fetchBankOptionSets } from "./ProfessionalContacts.helper";
import useStyles from "./ProfessionalContacts.styles";

const AddressRowsEditor = ({ addresses, onChange }) => {
  const classes = useStyles();
  const [countryOptions, setCountryOptions] = useState([]);

  useEffect(() => {
    fetchBankOptionSets().then(({ country }) => setCountryOptions(country));
  }, []);

  useEffect(() => {
    if (!countryOptions.length) return;
    const optionValues = new Set(countryOptions.map((option) => option.value));
    let changed = false;
    const mapped = addresses.map((row) => {
      if (!row.country || optionValues.has(row.country)) return row;
      const match = countryOptions.find(
        (option) => option.label === row.country
      );
      if (!match) return row;
      changed = true;
      return { ...row, country: match.value };
    });
    if (changed) onChange(mapped);
  }, [countryOptions, addresses]);

  const updateRow = (rowKey, field, value) => {
    onChange(
      addresses.map((row) =>
        row._key === rowKey ? { ...row, [field]: value } : row
      )
    );
  };

  const setPreferred = (rowKey) => {
    onChange(
      addresses.map((row) => ({ ...row, isPreferred: row._key === rowKey }))
    );
  };

  const addRow = () => {
    onChange([
      ...addresses,
      {
        _key: `new-${Date.now()}-${addresses.length}`,
        building: "",
        street: "",
        town: "",
        postcode: "",
        country: "United Kingdom",
        isPreferred: false,
      },
    ]);
  };

  const removeRow = (rowKey) => {
    if (addresses.length <= 1) return;
    const wasPreferred = addresses.find((r) => r._key === rowKey)?.isPreferred;
    const next = addresses.filter((row) => row._key !== rowKey);
    if (wasPreferred && next.length && !next.some((r) => r.isPreferred))
      next[0].isPreferred = true;
    onChange(next);
  };

  const FIELDS = [
    { key: "building", label: "Building *" },
    { key: "street", label: "Street *" },
    { key: "town", label: "Town *" },
    { key: "postcode", label: "Postcode *" },
  ];

  return (
    <div>
      <div className={classes.sectionHeading}>
        Addresses
        <button
          type="button"
          className={clsx(classes.addRowButton, classes.headingAction)}
          onClick={addRow}
        >
          <AddIcon fontSize="inherit" /> Add another address
        </button>
      </div>
      {addresses.map((row, index) => (
        <div key={row._key} className={classes.editorRow}>
          <div className={classes.editorRowHead}>
            <span className={classes.editorRowLabel}>Address {index + 1}</span>
            <div className={classes.editorRowActions}>
              <A_Switch
                label="Preferred"
                labelPlacement="end"
                size="small"
                checked={Boolean(row.isPreferred)}
                onChange={() => setPreferred(row._key)}
              />
              <IconAction
                danger
                title="Remove Address"
                icon={<DeleteIcon fontSize="small" />}
                disabled={addresses.length <= 1}
                onClick={() => removeRow(row._key)}
              />
            </div>
          </div>
          <div className={classes.editorGrid}>
            {FIELDS.map(({ key, label }) => (
              <A_TextField
                key={key}
                id={`address-${key}-${row._key}`}
                label={label}
                className={classes.field}
                defaultValue={row[key]}
                onChange={(e) => updateRow(row._key, key, e.target.value)}
                fullWidth
                size="small"
                margin="none"
                variant="outlined"
              />
            ))}
            <A_SimpleSelect
              id={`address-country-${row._key}`}
              label="Country *"
              className={classes.field}
              size="small"
              style={{ margin: 0, width: "100%" }}
              defaultValue={row.country || ""}
              options={countryOptions}
              optionLabel="label"
              optionValue="value"
              onChange={(value) => updateRow(row._key, "country", value)}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

AddressRowsEditor.propTypes = {
  addresses: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default AddressRowsEditor;
